/**
 * NotificationBell - Componente campanella per notifiche real-time
 * Mostra le ultime 10 notifiche con dropdown e lampeggio per nuove notifiche
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../context/NotificationsContext';
import { useColorPalette } from '../hooks/useColorPalette';
import moment from 'moment';

const NotificationBell = ({ size = 'normal' }) => {
    const navigate = useNavigate();
    const colors = useColorPalette();
    
    // Usa il context per le notifiche
    const {
        notifications,
        unreadCount,
        isFlashing,
        flashCount,
        markAsRead,
        markAllAsRead,
        stopFlashing
    } = useNotifications();
    
    // Dimensioni in base alla prop size
    const isSmall = size === 'small';
    const iconSize = isSmall ? '1rem' : '1.4rem';
    const badgeSize = isSmall ? '14px' : '18px';
    const badgeFontSize = isSmall ? '0.6rem' : '0.7rem';
    
    const [isOpen, setIsOpen] = useState(false);
    
    const dropdownRef = useRef(null);
    const bellRef = useRef(null);
    
    // Chiudi dropdown quando si clicca fuori
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                bellRef.current && !bellRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);
    
    // Gestione click sulla campanella
    const handleBellClick = () => {
        setIsOpen(!isOpen);
        
        // Se si apre, ferma il lampeggio
        if (!isOpen) {
            stopFlashing();
        }
    };
    
    // Gestione click su una notifica
    const handleNotificationClick = (notification) => {
        // Marca come letta
        markAsRead(notification.id);
        
        // Chiudi dropdown
        setIsOpen(false);
        
        // Naviga alla pagina appropriata
        if (notification.type === 'assistanceRequest' && notification.data?.assistanceRequestId) {
            navigate(`/assistanceRequestsNoAI/edit/${notification.data.assistanceRequestId}`);
        }
    };
    
    // Marca tutte come lette
    const handleMarkAllRead = (e) => {
        e.stopPropagation();
        markAllAsRead();
    };
    
    // Vai a tutte le notifiche
    const handleViewAll = () => {
        setIsOpen(false);
        navigate('/notifications');
    };
    
    // Determina il colore della campanella
    const getBellColor = () => {
        if (isFlashing && flashCount % 2 === 1) {
            return '#dc3545'; // Rosso durante il lampeggio
        }
        if (unreadCount > 0) {
            return colors.primary || '#3a5a40';
        }
        return '#6c757d'; // Grigio se nessuna notifica
    };
    
    // Icona della notifica in base al tipo
    const getNotificationIcon = (type) => {
        switch (type) {
            case 'assistanceRequest':
                return 'fa-solid fa-wrench';
            case 'appointment':
                return 'fa-solid fa-calendar';
            case 'job':
                return 'fa-solid fa-briefcase';
            default:
                return 'fa-solid fa-bell';
        }
    };
    
    return (
        <div style={{ position: 'relative', display: 'inline-block' }}>
            {/* Campanella */}
            <button
                ref={bellRef}
                onClick={handleBellClick}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    padding: isSmall ? '4px' : '8px',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    position: 'relative',
                    transform: isFlashing && flashCount % 2 === 1 ? 'scale(1.2)' : 'scale(1)',
                }}
                title="Notifiche"
            >
                <i 
                    className="fa-solid fa-bell" 
                    style={{ 
                        fontSize: iconSize,
                        color: getBellColor(),
                        transition: 'color 0.15s ease',
                    }}
                />
                
                {/* Badge contatore */}
                {unreadCount > 0 && (
                    <span
                        style={{
                            position: 'absolute',
                            top: isSmall ? '0' : '2px',
                            right: isSmall ? '0' : '2px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            borderRadius: '50%',
                            minWidth: badgeSize,
                            height: badgeSize,
                            fontSize: badgeFontSize,
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: '0 2px',
                            animation: isFlashing ? 'pulse 0.3s ease-in-out' : 'none'
                        }}
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>
            
            {/* Dropdown notifiche */}
            {isOpen && (
                <div
                    ref={dropdownRef}
                    style={{
                        position: 'absolute',
                        top: '100%',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '320px',
                        maxHeight: '400px',
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        overflow: 'hidden',
                        marginTop: '8px'
                    }}
                >
                    {/* Header */}
                    <div
                        style={{
                            padding: '12px 16px',
                            borderBottom: '1px solid #e9ecef',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            backgroundColor: colors.primary || '#3a5a40',
                            color: 'white'
                        }}
                    >
                        <span style={{ fontWeight: 'bold' }}>
                            <i className="fa-solid fa-bell me-2"></i>
                            Notifiche
                        </span>
                        {unreadCount > 0 && (
                            <button
                                onClick={handleMarkAllRead}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.8rem',
                                    opacity: 0.8
                                }}
                            >
                                Segna tutte lette
                            </button>
                        )}
                    </div>
                    
                    {/* Lista notifiche */}
                    <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {notifications.length === 0 ? (
                            <div
                                style={{
                                    padding: '24px',
                                    textAlign: 'center',
                                    color: '#6c757d'
                                }}
                            >
                                <i className="fa-solid fa-inbox" style={{ fontSize: '2rem', marginBottom: '8px', display: 'block' }}></i>
                                Nessuna notifica
                            </div>
                        ) : (
                            notifications.map((notification, index) => (
                                <div
                                    key={notification.id || index}
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{
                                        padding: '12px 16px',
                                        borderBottom: '1px solid #f0f0f0',
                                        cursor: 'pointer',
                                        backgroundColor: notification.read ? 'white' : '#f8f9fa',
                                        transition: 'background-color 0.2s',
                                        display: 'flex',
                                        gap: '12px',
                                        alignItems: 'flex-start'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e9ecef'}
                                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = notification.read ? 'white' : '#f8f9fa'}
                                >
                                    {/* Icona */}
                                    <div
                                        style={{
                                            width: '36px',
                                            height: '36px',
                                            borderRadius: '50%',
                                            backgroundColor: notification.data?.createdByCustomer 
                                                ? '#9b59b6' 
                                                : (colors.primary || '#3a5a40'),
                                            color: 'white',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            flexShrink: 0
                                        }}
                                    >
                                        <i className={notification.data?.createdByCustomer 
                                            ? 'fa-solid fa-mobile-screen' 
                                            : getNotificationIcon(notification.type)} 
                                           style={{ fontSize: '0.9rem' }}
                                        />
                                    </div>
                                    
                                    {/* Contenuto */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ 
                                            fontWeight: notification.read ? 'normal' : 'bold',
                                            fontSize: '0.9rem',
                                            marginBottom: '2px'
                                        }}>
                                            {notification.title}
                                        </div>
                                        <div style={{ 
                                            fontSize: '0.8rem', 
                                            color: '#6c757d',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {notification.message}
                                        </div>
                                        <div style={{ 
                                            fontSize: '0.75rem', 
                                            color: '#adb5bd',
                                            marginTop: '4px'
                                        }}>
                                            {moment(notification.createdAt).fromNow()}
                                        </div>
                                    </div>
                                    
                                    {/* Indicatore non letto */}
                                    {!notification.read && (
                                        <div
                                            style={{
                                                width: '8px',
                                                height: '8px',
                                                borderRadius: '50%',
                                                backgroundColor: colors.primary || '#3a5a40',
                                                flexShrink: 0,
                                                marginTop: '6px'
                                            }}
                                        />
                                    )}
                                </div>
                            ))
                        )}
                    </div>
                    
                    {/* Footer */}
                    <div
                        style={{
                            padding: '12px 16px',
                            borderTop: '1px solid #e9ecef',
                            textAlign: 'center'
                        }}
                    >
                        <button
                            onClick={handleViewAll}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: colors.primary || '#3a5a40',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '0.9rem'
                            }}
                        >
                            Vedi tutte le notifiche
                            <i className="fa-solid fa-arrow-right ms-2"></i>
                        </button>
                    </div>
                </div>
            )}
            
            {/* CSS per animazione pulse */}
            <style>{`
                @keyframes pulse {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(1.2); }
                }
            `}</style>
        </div>
    );
};

export default NotificationBell;
